import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, FileTextIcon, ListChecksIcon, MessageSquareIcon, VideoIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Container } from '../../components/ui/Container';
import { GeometricAccent } from '../../components/ui/GeometricAccent';
import { MosqueArchMask } from '../../components/ui/MosqueArchMask';
import { PatternDecor } from '../../components/ui/PatternDecor';
type Tone = 'sage' | 'sky' | 'rose' | 'sand';
type Resource = {
  icon: typeof FileTextIcon;
  title: string;
  description: string;
  tone: Tone;
};
const toneClasses: Record<Tone, string> = {
  sage: 'border-forest-700/10 bg-sage-100',
  sky: 'border-[#C7D4E0]/40 bg-[#EDF2F7]',
  rose: 'border-[#E0BFB3]/40 bg-[#F7EDE9]',
  sand: 'border-sand-300/40 bg-sand-100'
};
const resources: Resource[] = [{
  icon: FileTextIcon,
  title: 'أدلة عملية',
  description: 'ملفات خطوة بخطوة للمواقف الشائعة: اجتماعات المدرسة وأسئلة الهوية والمحادثات الحساسة.',
  tone: 'sage'
}, {
  icon: VideoIcon,
  title: 'محاضرات مسجلة من المختصين',
  description: 'جلسات قصيرة ومركّزة يمكنك مشاهدتها عند الحاجة، دون حاجة إلى قراءة إضافية.',
  tone: 'sky'
}, {
  icon: MessageSquareIcon,
  title: 'أدوات للحوار الأسري',
  description: 'نصوص وأسئلة تساعدك على إجراء محادثات هادئة وصادقة مع طفلك حول الموضوعات الصعبة.',
  tone: 'rose'
}, {
  icon: ListChecksIcon,
  title: 'قوائم تحقق وقوالب',
  description: 'قوالب للتوثيق وقوائم لتواصل المدرسة وبطاقات مرجعية سريعة للحظات المرهقة.',
  tone: 'sand'
}];
const principles = ['مراعية للاختلافات الثقافية', 'مرتكزة إلى الإيمان وغير سياسية', 'سهلة التطبيق في الحياة اليومية', 'آمنة للأطفال والوالدين', 'تركز على الكرامة والاندماج'];
export function ArabicResourcesGuidance() {
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-4343" data-mp="16xjxd4706ty6">
      <section className="relative isolate overflow-hidden" data-id="element-4344" data-mp="16x0ijwcnglod">
        <GeometricAccent variant="arabesque" className="absolute -right-20 top-6 hidden h-[420px] w-[420px] md:block" opacity={0.08} data-id="element-4345" data-mp="1e19rgrx7z2c4" />
        <GeometricAccent variant="dots" className="absolute left-8 top-16 h-32 w-32" opacity={0.4} data-id="element-4346" data-mp="1nyxkndf6g1s6" />
        <Container className="relative pb-16 pt-8 md:pb-24 md:pt-12" data-id="element-4347" data-mp="1sxh6ldtrtdkj">
          <div className="grid items-center gap-10 lg:grid-cols-12" data-id="element-4348" data-mp="1svo8wo14y3oo">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} className="lg:col-span-6" data-id="element-4349" data-mp="vbbkyg92lhal">
              <span className="text-sm font-semibold text-forest-700" data-id="element-4350" data-mp="19ywjrxywwucp">دعم عملي للتربية اليومية</span>
              <h1 className="mt-3 font-display text-4xl font-semibold leading-[1.45] text-ink-900 sm:text-5xl lg:text-[3.5rem]" data-id="element-4351" data-mp="cjip8e2m9hz6">الموارد والإرشاد</h1>
              <div className="mt-6 max-w-xl space-y-3 text-base leading-loose text-ink-700 md:text-lg" data-id="element-4352" data-mp="hhaqvzt2cigk">
                <p data-id="element-4353" data-mp="2urk72z952lcw">يحصل الوالدان على موارد منتقاة بعناية، صُممت للأسر المسلمة التي تعيش في بيئات غير مسلمة.</p>
                <p data-id="element-4354" data-mp="2qs1podgj0set">كل مادة تراعي الثقافة، وغير سياسية، وسهلة التطبيق في الحياة اليومية.</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-4355" data-mp="2lu9yjlnphabo">
                <a href="#content" data-id="element-4356" data-mp="2gs12uhjg7u5v"><Button variant="primary" size="lg" data-id="element-4357" data-mp="2gsklk0gxamky">استكشف الموارد</Button></a>
                <Link to="/ar/parents/preventing-discrimination" data-id="element-4358" data-mp="2bv18h3vyoful"><Button variant="secondary" size="lg" data-id="element-4359" data-mp="2bw3uoyic9u46">الوقاية من التمييز</Button></Link>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-4360" data-mp="23161rrf6yhcu">
              <div className="relative aspect-[200/148] w-full lg:-mr-8 lg:w-[calc(100%+6rem)]" data-id="element-4361" data-mp="1t5cf3f7rd7hc">
                <MosqueArchMask src="https://cdn.magicpatterns.com/uploads/cHe8qMcmyi3psE64ERz1VF/Resources-7.jpg" alt="جهاز لوحي يعرض مادة إرشادية على مكتب مضاء بجانب نظارات وكوب شاي" data-id="element-4362" data-mp="1o6rfni0byrty" />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      <section id="content" className="scroll-mt-24 bg-sage-50/60 py-16 md:py-24" data-id="element-4363" data-mp="3jffynxk3b0n7">
        <Container data-id="element-4364" data-mp="35ggbyvg50vto">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-4365" data-mp="35fx4k8o45ptw">ما الذي تحصل عليه</h2>
          <p className="mt-5 max-w-3xl leading-loose text-ink-700" data-id="element-4366" data-mp="3ag1etgyfhobo">مكتبة من المواد العملية التي يمكنك استخدامها في المنزل لتشرح وتستعد وتدعم طفلك في مواقف الحياة اليومية.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-4367" data-mp="30gah76ekmt7d">
            {resources.map((resource, index) => <ResourceCard key={resource.title} resource={resource} index={index} data-id="element-4368" data-mp="tqe19bzjstxq" data-mpi={index} />)}
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-white/60 py-16 md:py-24" data-id="element-4369" data-mp="yp766g0rrcma">
        <PatternDecor side="left" y="top" size="lg" data-id="element-4370" data-mp="2j8jc3acn7dlj" />
        <Container data-id="element-4371" data-mp="2e8yasta02t5n">
          <div className="grid items-center gap-12 lg:grid-cols-12" data-id="element-4372" data-mp="2e9g5vmkxf1bz">
            <div className="lg:col-span-6" data-id="element-4373" data-mp="2sxkqiw3jhmxe">
              <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-4xl" data-id="element-4374" data-mp="2sycmi811tmo7">مصممة لتخفيف التوتر، لا لزيادته</h2>
              <p className="mt-5 leading-loose text-ink-700" data-id="element-4375" data-mp="2o51alshdqfpf">كل مورد قصير وعملي ومكتوب للوالدين المشغولين. لا مصطلحات معقدة ولا سياسة ولا ضغط، بل إرشاد واضح قابل للتطبيق اليوم.</p>
              <p className="mt-4 leading-loose text-ink-700" data-id="element-4376" data-mp="2xwe5ksyh5lzo">صُمم كل شيء لمساعدتك على الاستجابة بثقة، دون تصعيد أو مواجهة.</p>
            </div>
            <div className="lg:col-span-6" data-id="element-4377" data-mp="36xlskevjj0eg">
              <div className="rounded-2xl border border-sage-200 bg-sage-50 p-7 shadow-soft md:p-8" data-id="element-4378" data-mp="36xvg7vt44vgz">
                <p className="mb-4 text-sm font-semibold text-forest-700" data-id="element-4379" data-mp="1hixn6xldaipx">كل المواد مصممة لتكون:</p>
                <ul className="space-y-3" data-id="element-4380" data-mp="1ckvz1y933t7l">
                  {principles.map(principle => <li key={principle} className="flex items-center gap-3 leading-loose text-ink-700" data-id="element-4381" data-mp="3cn57hhgdfnof">
                      <CheckCircle2Icon size={16} className="shrink-0 text-forest-700" strokeWidth={1.6} aria-hidden="true" data-id="element-4382" data-mp="3hkxap7mguw7x" />
                      <span data-id="element-4383" data-mp="3qlbws9798fch">{principle}</span>
                    </li>)}
                </ul>
                <p className="mt-6 border-t border-sage-200 pt-5 text-sm leading-loose text-ink-500" data-id="element-4384" data-mp="3vkoi2pih9ecy">نقدّم التثقيف والإرشاد، ولا نقدّم تمثيلًا قانونيًا.</p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-[#F7EDE9]/60 py-16 md:py-24" data-id="element-4385" data-mp="9652wqlohv5f">
        <Container data-id="element-4386" data-mp="d676x6evz0nv">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between" data-id="element-4387" data-mp="d6p1zzptb8u7">
            <div className="max-w-xl" data-id="element-4388" data-mp="i6bdo4pzsh0w">
              <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-4389" data-mp="i7d35e7r9625">احصل على الموارد التي تحتاجها</h2>
              <p className="mt-5 leading-loose text-ink-700" data-id="element-4390" data-mp="x8a5kxcsctgx">تمنحك العضوية الوصول إلى المكتبة الكاملة، إلى جانب جلسات شهرية مع المختصين ودعم مباشر.</p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-4391" data-mp="sbjoq7t5ptug">
              <Link to="/ar/waitlist" data-id="element-4392" data-mp="n9m0ez5wg8un"><Button variant="primary" data-id="element-4393" data-mp="nb5j0wf12vzl">انضم إلى العضوية</Button></Link>
              <Link to="/ar/contact" data-id="element-4394" data-mp="j9k83gm20rs1"><Button variant="secondary" data-id="element-4395" data-mp="ja3ogsw2383y">تواصل معنا</Button></Link>
            </div>
          </div>
        </Container>
      </section>
    </div>;
}
function ResourceCard({
  resource,
  index
}: {
  resource: Resource;
  index: number;
}) {
  const Icon = resource.icon;
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.06
  }} className={`flex gap-4 rounded-2xl border p-6 shadow-soft ${toneClasses[resource.tone]}`} data-id="element-4396" data-mp="168gee5xjh59r">
      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-forest-700/10 bg-white text-forest-700" data-id="element-4397" data-mp="1txrjfzlx9ltp">
        <Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-4398" data-mp="2dl07lk6o096a" />
      </span>
      <div data-id="element-4399" data-mp="2if4i2df52zc4">
        <h3 className="font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-4400" data-mp="2ifnx9mden9mg">{resource.title}</h3>
        <p className="mt-1 text-sm leading-loose text-ink-700" data-id="element-4401" data-mp="23mu633ort51y">{resource.description}</p>
      </div>
    </motion.article>;
}